// Lazy-loaded interactive 3D viewer for the real coffee-table model. Nothing
// here runs until the visitor clicks "View in 3D" — three.js plus the OBJ
// geometry and its 3 PBR textures only load on demand, so the homepage stays
// light by default, especially on phones.
//
// Uses the real textured OBJ (with diffuse/roughness/metallic maps) rather
// than the plain untextured STL used in the first version of this viewer —
// materials are built directly in JS from explicit, fully-resolved asset
// URLs (window.ConceptViewer*Url, set by the Liquid section) rather than
// relying on MTLLoader's relative-path resolution, which isn't guaranteed to
// match Shopify's fingerprinted CDN asset URLs.
//
// Lessons carried over from the earlier (scrapped) frames-generator project,
// applied proactively instead of re-discovering them the hard way:
//   - Materials are DoubleSide from the start — a previous STL conversion
//     from the same source pipeline had triangle winding that didn't match
//     three.js's FrontSide convention and rendered solid black.
//   - Don't assume a fixed up-axis. This mesh may be Z-up (common for
//     print-oriented exports) instead of three.js's default Y-up. Detected
//     via a bounding-box heuristic (a coffee table is far wider/deeper than
//     it is tall, so the SMALLEST bounding-box extent is almost certainly
//     the vertical axis in the file's own coordinate system).
//   - camera.up must be set BEFORE constructing OrbitControls — it caches
//     the up vector at construction time and ignores later reassignment.
(function () {
  var button = document.getElementById('ConceptViewerButton');
  var poster = document.getElementById('ConceptViewerPoster');
  var stage = document.getElementById('ConceptViewerStage');
  if (!button || !stage) return;

  var CDN = {
    three: 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js',
    controls: 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/OrbitControls.js',
    obj: 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/OBJLoader.js',
  };

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function loadThree() {
    if (window.THREE && window.THREE.OrbitControls && window.THREE.OBJLoader) {
      return Promise.resolve();
    }
    return loadScript(CDN.three)
      .then(function () { return Promise.all([loadScript(CDN.controls), loadScript(CDN.obj)]); });
  }

  function initViewer() {
    var canvasWrap = document.createElement('div');
    canvasWrap.className = 'concept-viewer__canvas-wrap';
    stage.innerHTML = '';
    stage.appendChild(canvasWrap);

    var width = canvasWrap.clientWidth || stage.clientWidth;
    var height = 420;

    var renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setClearColor(0x000000, 0);
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    canvasWrap.appendChild(renderer.domElement);

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(40, width / height, 1, 5000);

    scene.add(new THREE.HemisphereLight(0xfff4e0, 0x201812, 0.9));
    var key = new THREE.DirectionalLight(0xffffff, 1.0);
    key.position.set(80, 120, 150);
    scene.add(key);
    var fill = new THREE.DirectionalLight(0xbcd4ff, 0.35);
    fill.position.set(-100, 60, -80);
    scene.add(fill);

    var controls = new THREE.OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;

    var texLoader = new THREE.TextureLoader();
    var diffuseMap = texLoader.load(window.ConceptViewerDiffuseUrl);
    diffuseMap.encoding = THREE.sRGBEncoding;
    var material = new THREE.MeshStandardMaterial({
      map: diffuseMap,
      roughnessMap: texLoader.load(window.ConceptViewerRoughnessUrl),
      metalnessMap: texLoader.load(window.ConceptViewerMetallicUrl),
      roughness: 1,
      metalness: 1,
      side: THREE.DoubleSide,
    });

    new THREE.OBJLoader().load(
      window.ConceptViewerObjUrl,
      function (group) {
        group.traverse(function (child) {
          if (child.isMesh) child.material = material;
        });

        var box = new THREE.Box3().setFromObject(group);
        var size = new THREE.Vector3();
        box.getSize(size);

        // whichever axis has the smallest extent is almost certainly "up"
        // for a piece of furniture this shape (much wider/deeper than tall)
        var axes = [
          { axis: 'x', extent: size.x },
          { axis: 'y', extent: size.y },
          { axis: 'z', extent: size.z },
        ].sort(function (a, b) { return a.extent - b.extent; });
        var upAxis = axes[0].axis;

        if (upAxis === 'z') {
          group.rotateX(-Math.PI / 2);
        } else if (upAxis === 'x') {
          group.rotateZ(Math.PI / 2);
        }

        var box2 = new THREE.Box3().setFromObject(group);
        var center = new THREE.Vector3();
        box2.getCenter(center);
        group.position.sub(center);

        scene.add(group);

        var sphere = new THREE.Sphere();
        box2.getBoundingSphere(sphere);
        var radius = sphere.radius || 5;

        var dist = radius * 2.4;
        camera.position.set(dist * 0.7, dist * 0.55, dist * 0.9);
        camera.up.set(0, 1, 0);
        camera.lookAt(0, 0, 0);
        controls.target.set(0, 0, 0);
        controls.update();

        poster.remove();
      },
      undefined,
      function (err) {
        canvasWrap.innerHTML = '<p class="concept-viewer__error">Couldn\'t load the 3D model (' + (err && err.message ? err.message : 'unknown error') + ').</p>';
      }
    );

    function animate() {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    }
    animate();

    window.addEventListener('resize', function () {
      var w = canvasWrap.clientWidth || stage.clientWidth;
      renderer.setSize(w, height);
      camera.aspect = w / height;
      camera.updateProjectionMatrix();
    });
  }

  button.addEventListener('click', function () {
    button.disabled = true;
    button.textContent = 'Loading…';
    loadThree()
      .then(initViewer)
      .catch(function () {
        button.disabled = false;
        button.textContent = 'View in 3D';
      });
  });
})();
